'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAppDispatch } from 'src/store/client/config';
import { modalActions } from 'src/store/client/slices/modalSlice';
import { encryptPlainText } from 'src/utils/auth';

import { useTeamConfig } from '@/app/(football)/[team]/_context/context';
import { useMiniGamePageConfig } from '@/app/(football)/[team]/mini-game/_context/context';
import { trackEvent } from '@/libs/analytics/events';
import {
  USE_FOOTBALL_QUERY_KEY,
  useFinishBlockMiniGameMutation,
  useGetBlockMiniGameInitWithInitQuery,
} from '@/store/server/common/football/football.query';
import { USE_POINT_QUERY_KEY } from '@/store/server/common/point/point.query';

import GameResultModal from './GameResultModal';
import { InstructionsOverlay } from './InstructionsOverlay';
import { LoadingOverlay } from './LoadingOverlay';
import { ScoreDisplay } from './ScoreDisplay';
import type { GameState } from './types';

interface GameUIProps {
  score: number;
  gameState: GameState;
  onStartGame: () => void;
  onResetGame: () => void;
}

/**
 * Main game UI component
 * Manages score display, loading screen, instructions screen, and game over screen
 */
export function GameUI({ score, gameState, onStartGame, onResetGame }: GameUIProps) {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { originalsName } = useTeamConfig();
  const { eventPeriod } = useMiniGamePageConfig();
  const isEventEnded = new Date() > new Date(eventPeriod.endDate);

  const {
    data: initData,
    originalsIdx,
    isLoading: isInitLoading,
  } = useGetBlockMiniGameInitWithInitQuery({ originalsName });

  const applyCount = initData?.applyCount ?? 0;

  const { mutate: finishBlockMiniGame } = useFinishBlockMiniGameMutation({
    onSuccess: ({ data }) => {
      const { amount, point, rank, apply_count } = data.data;
      trackEvent({
        name: 'complete_mini_game',
        params: {
          game_name: 'block_stacking',
          score: amount,
          point,
          rank,
        },
      });
      if (point > 0) {
        trackEvent({
          name: 'earning_point',
          params: {
            point,
            value: point,
            action_type: 'mini-game',
            migration_source: 'gtm',
          },
        });
      }
      dispatch(
        modalActions.openComponentModal({
          type: 'strong',
          component: (
            <GameResultModal score={amount} point={point} rank={rank} remainingLives={10 - apply_count} onResetGame={onResetGame} />
          ),
        }),
      );
      queryClient.invalidateQueries({ queryKey: [USE_POINT_QUERY_KEY.getMyPoint] });
      queryClient.invalidateQueries({ queryKey: [USE_FOOTBALL_QUERY_KEY.getBlockMiniGameRanking, originalsIdx] });
      queryClient.invalidateQueries({ queryKey: [USE_FOOTBALL_QUERY_KEY.getBlockMiniGameInit, originalsIdx] });
    },
    onError: () => {
      dispatch(
        modalActions.openAlertModal({
          icon: 'danger',
          title: ['접속에 문제가 발생했어요.', '잠시 후 다시 시도해 주세요.'],
          onClose: () => {
            onResetGame();
          },
        }),
      );
    },
  });

  const handleFinishGame = async () => {
    const encryptedAmount = await encryptPlainText(String(score));
    finishBlockMiniGame({
      originalsIdx,
      amount: encryptedAmount,
    });
  };

  useEffect(() => {
    if (gameState === 'playing') {
      trackEvent({
        name: 'play_game',
        params: { game_name: 'block_stacking' },
      });
    }
    if (gameState === 'gameOver') {
      handleFinishGame();
    }
  }, [gameState]);

  const isLoading = gameState === 'loading' || isInitLoading;
  const showInstructions = gameState === 'instructions' && !isInitLoading;

  return (
    <>
      {/* Score display - only show when not loading or in instructions */}
      {gameState !== 'loading' && gameState !== 'instructions' && <ScoreDisplay score={score} />}

      {/* Game loading screen */}
      {isLoading && <LoadingOverlay />}

      {/* Game start instructions screen */}
      {showInstructions && (
        <InstructionsOverlay
          onStartGame={onStartGame}
          remainingLives={10 - applyCount}
          isExhausted={applyCount >= 10}
          isEventEnded={isEventEnded}
        />
      )}
    </>
  );
}
